import * as React from 'react';
import { useState } from 'react';
import styled from '@emotion/styled/macro';
import { ReactComponent as ErrorIllustration } from '@arnold/common/lib/assets/illustrations/ErrorIllustration.svg';
import { getFrontendConfigValue, Modal } from '@arnold/common';
import { Trans, useTranslation } from 'react-i18next';
import { useIsAccessTokenValidQuery, useLogErrorReportMutation } from '../generated/hooks';
import { isValidEmail } from '../lib/helpers';
import { theme } from '@arnold/common/lib/theme';
import storage from '../lib/storage';
import { useHistory } from 'react-router-dom';

// steps for the modal
enum ErrorStep {
  INITIAL = 0,
  EMAIL_INPUT = 1,
  CONFIRMATION = 2,
}

interface ErrorModalProps {
  errorDetails: {
    error: Error;
    componentStack: string;
  };
  onClose?: () => void;
}

// Styled components
const InputContainer = styled.div`
  margin-top: ${theme.spacing.f};
  margin-bottom: ${theme.spacing.f};
`;

const InputLabel = styled.label`
  ${theme.typography.body.small.regular};
  margin-left: ${theme.spacing.f};
  margin-bottom: ${theme.spacing.c};
  display: block;
`;

const StyledInput = styled.input<{ isInvalid?: boolean }>`
  width: 100%;
  padding: ${theme.spacing.d} ${theme.spacing.f};
  border-radius: ${theme.borderRadius.m};
  border: 1px solid ${({ isInvalid }) => (isInvalid ? theme.colors.form.error : theme.colors.borderMain.default)};
  color: ${theme.colors.emotionNeutral.default};
  outline: none;

  &:focus {
    border-color: ${theme.colors.borderMain.focused};
  }
`;

const FormError = styled.div`
  ${theme.typography.body.small.regular};
  margin-left: ${theme.spacing.f};
  margin-top: ${theme.spacing.c};
  margin-bottom: ${theme.spacing.d};
  color: ${theme.colors.form.error};
`;

// error modal
export const ErrorModal: React.FC<ErrorModalProps> = ({ errorDetails, onClose }) => {
  const { t } = useTranslation(['errorModal']);
  const [currentStep, setCurrentStep] = useState<ErrorStep>(ErrorStep.INITIAL);
  const [email, setEmail] = useState('');
  const [emailValid, setEmailValid] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);
  const [show, setShow] = useState(true);

  const { data } = useIsAccessTokenValidQuery();

  const [logErrorReport, { loading: isSending }] = useLogErrorReportMutation();
  const history = useHistory();

  // Handle email changes
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEmail = e.target.value;
    setEmail(newEmail);
    setEmailValid(isValidEmail(newEmail));
  };

  const handleEmailBlur = () => {
    setEmailTouched(true);
  };

  // Handle modal close
  const handleModalClose = () => {
    setCurrentStep(ErrorStep.INITIAL);
    storage.setErrorDetails({
      name: errorDetails.error.name,
      message: errorDetails.error.message,
      timestamp: new Date().getTime(),
    });
    onClose?.();
    setShow(false);
  };

  // Handle modal submit

  const handleModalSubmit = async () => {
    const errorReport = {
      componentStack: errorDetails.componentStack,
      error: errorDetails.error.message,
      email: data?.isAccessTokenValid?.user?.username || email,
      organizationId: data?.isAccessTokenValid?.user?.organization?.id,
      systemRole: data?.isAccessTokenValid?.user?.systemRole,
      url: window.location.href,
      browserData: JSON.stringify({
        language: window.navigator.language,
        userAgent: window.navigator.userAgent,
        userAgentData: (window.navigator as any).userAgentData,
      }),
    };
    if (currentStep === ErrorStep.INITIAL && data?.isAccessTokenValid?.user?.username) {
      setCurrentStep(ErrorStep.CONFIRMATION);
      logErrorReport({
        variables: {
          errorReport,
        },
      });
    } else if (currentStep === ErrorStep.INITIAL && !data?.isAccessTokenValid?.user?.username) {
      setCurrentStep(ErrorStep.EMAIL_INPUT);
    } else if (currentStep === ErrorStep.EMAIL_INPUT && emailValid) {
      setCurrentStep(ErrorStep.CONFIRMATION);
      logErrorReport({
        variables: {
          errorReport,
        },
      });
    } else if (currentStep === ErrorStep.CONFIRMATION) {
      handleModalClose();
    }
  };

  // Dynamic modal props based on current step
  let title = '';
  let text: string | React.ReactNode = '';
  let content = null;
  let submitButtonTitle = isSending ? t('submitButtonTitleIsSubmitting') : t('submitButtonTitle');
  let cancelButtonTitle = t('cancelButtonTitle');
  let submitDisabled = false;

  const storedErrorDetails = storage.getErrorDetails();

  if (
    storedErrorDetails?.timestamp &&
    errorDetails?.error.name === storedErrorDetails?.name &&
    errorDetails?.error.message === storedErrorDetails?.message &&
    new Date().getTime() - new Date(storedErrorDetails?.timestamp).getTime() < 1000 * 60 * 1 &&
    history.location.pathname !== '/overview'
  ) {
    window.location.replace(`${getFrontendConfigValue('SELF_CARE_URL')}/overview`);
    return null;
  }

  switch (currentStep) {
    case ErrorStep.INITIAL:
      title = t('titleStep1');
      text = t('descriptionStep1');
      break;
    case ErrorStep.EMAIL_INPUT:
      title = t('titleStep2');
      text = t('descriptionStep2');
      submitDisabled = isSending;

      // Email input with validation
      content = (
        <InputContainer>
          <InputLabel htmlFor="email">Email</InputLabel>
          <StyledInput
            id="email"
            type="email"
            value={email}
            onChange={handleEmailChange}
            onBlur={handleEmailBlur}
            isInvalid={emailTouched && !emailValid}
            disabled={isSending}
          />
          {emailTouched && !emailValid && email && <FormError>{t('common:emailInvalidMessage')}</FormError>}
        </InputContainer>
      );

      // Disable submit button if email is invalid
      submitDisabled = isSending || !emailValid;
      break;
    case ErrorStep.CONFIRMATION:
      title = t('titleStep3');
      text = (
        <Trans
          i18nKey="errorModal:descriptionStep3"
          values={{ email: data?.isAccessTokenValid?.user?.username || email }}
          components={[<b />]}
        />
      );

      submitButtonTitle = t('submitButtonTitleCloseModal');
      cancelButtonTitle = '';
      break;
    default:
      console.warn('Unknown step:', currentStep);
      break;
  }

  return (
    <Modal
      hideAfterSubmit={false}
      icon={ErrorIllustration}
      title={title}
      text={text}
      content={content}
      onClose={handleModalClose}
      onSubmit={handleModalSubmit}
      onHide={handleModalClose}
      buttons={{
        submit: {
          title: submitButtonTitle,
          disabled: submitDisabled,
        },
        cancel: {
          title: cancelButtonTitle,
          show: currentStep !== ErrorStep.CONFIRMATION,
        },
      }}
      show={show}
    />
  );
};
